import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, ArrowLeft, ExternalLink, X, PlayCircle, Loader2, CheckCircle2, PictureInPicture2 } from "lucide-react";
import MemberLayout from "@/components/app/MemberLayout";
import memberApi from "@/lib/memberApi";

export default function WatchPage() {
  const { videoId } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showMini, setShowMini] = useState(false);
  const [dismissedCtas, setDismissedCtas] = useState(new Set());
  const [showPostActions, setShowPostActions] = useState(false);
  const [nativePipActive, setNativePipActive] = useState(false);
  const [affiliateLink, setAffiliateLink] = useState(null); // { affiliate_url, affiliate_id, source, lead_id }

  const iframeRef = useRef(null);          // YouTube iframe host div
  const playerRef = useRef(null);          // YT.Player instance
  const containerRef = useRef(null);       // main player wrapper (for scroll detection)
  const nativeVideoRef = useRef(null);     // <video> element for direct sources
  const lastReportedRef = useRef(0);
  const durationRef = useRef(0);

  const isDirect = video?.source_type === "direct" && video?.video_url;
  const isYouTube = !isDirect && !!video?.youtube_id;

  // Resolve the sticky affiliate URL for this member (Funnel B).
  // Runs once on mount; the answer is stable across videos for the same session.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await memberApi.get("/community/affiliate-link");
        setAffiliateLink(data);
      } catch (err) {
        // Non-fatal — fallback links from admin will be used as-is
        console.warn("affiliate-link resolve failed", err);
      }
    })();
  }, []);

  // Given a CTA-like object, return the URL that should actually be opened.
  // Rule: if `use_affiliate_link` is true (default) AND we have an affiliate_url,
  // return the affiliate URL. Otherwise fall back to the admin-configured link.
  const resolveLink = (obj, fallback) => {
    const affUrl = affiliateLink?.affiliate_url;
    const useAff = obj?.use_affiliate_link !== false; // default true when field missing
    if (useAff && affUrl) return affUrl;
    return obj?.link_url || obj?.url || fallback || "#";
  };

  // Fire analytics for a CTA click. Always emits `vod_cta_click` (all clicks);
  // additionally emits `affiliate_link_click` for backward-compat when the click
  // was resolved to the sticky affiliate URL — that separate event powers the
  // legacy affiliate reports.
  const trackCtaClick = (source, cta, targetUrl) => {
    const isAff = cta?.use_affiliate_link !== false && !!affiliateLink?.affiliate_url;
    const url = targetUrl || (isAff ? affiliateLink?.affiliate_url : cta?.link_url) || null;
    const payload = {
      source,                    // "cta_overlay" | "post_cta" | "resource"
      video_id: video?.id || null,
      video_title: video?.title || null,
      cta_id: cta?.id || null,
      cta_text: cta?.text || null,
      is_affiliate: isAff,
      affiliate_id: isAff ? (affiliateLink?.affiliate_id || null) : null,
      resolution: isAff ? (affiliateLink?.source || "unknown") : "static",
      target: url,
    };
    try {
      memberApi.post("/analytics/event", {
        event_type: "vod_cta_click",
        lead_id: affiliateLink?.lead_id || null,
        campaign_id: null,
        payload,
      }).catch(() => {});
      if (isAff) {
        memberApi.post("/analytics/event", {
          event_type: "affiliate_link_click",
          lead_id: affiliateLink?.lead_id || null,
          campaign_id: null,
          payload,
        }).catch(() => {});
      }
    } catch (_) { /* noop */ }
  };


  // Load video metadata
  useEffect(() => {
    setLoading(true);
    setDismissedCtas(new Set());
    setShowPostActions(false);
    setCurrentTime(0);
    (async () => {
      try {
        const { data } = await memberApi.get(`/community/videos/${videoId}`);
        setVideo(data);
      } catch (err) {
        console.error("video load failed", err);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
  }, [videoId]);

  // Load YouTube iframe API once (only if we might use it)
  useEffect(() => {
    if (!isYouTube) return;
    if (window.YT && window.YT.Player) return;
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
  }, [isYouTube]);

  // === YouTube player lifecycle ===
  useEffect(() => {
    if (!isYouTube || !video?.youtube_id) return;
    let cancelled = false;
    const init = () => {
      if (cancelled || !iframeRef.current) return;
      // eslint-disable-next-line no-undef
      playerRef.current = new YT.Player(iframeRef.current, {
        videoId: video.youtube_id,
        playerVars: {
          rel: 0, modestbranding: 1, playsinline: 1, cc_load_policy: 0,
          origin: window.location.origin,
          start: Math.floor(video.progress_seconds || 0),
        },
        events: {
          onReady: (e) => { durationRef.current = e.target.getDuration(); },
          onStateChange: (e) => {
            setIsPlaying(e.data === 1);
            if (e.data === 0) {
              setShowPostActions(true);
              memberApi.post(`/community/videos/${videoId}/progress`, {
                current_time_seconds: durationRef.current || 0,
                is_completed: true,
              }).catch(() => {});
            }
          },
        },
      });
    };
    if (window.YT && window.YT.Player) init();
    else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev && prev(); init(); };
    }
    return () => {
      cancelled = true;
      if (playerRef.current?.destroy) {
        try { playerRef.current.destroy(); } catch (_) {}
      }
      playerRef.current = null;
    };
  }, [isYouTube, video?.youtube_id, videoId, video?.progress_seconds]);

  // === Native <video> lifecycle ===
  useEffect(() => {
    if (!isDirect) return;
    const el = nativeVideoRef.current;
    if (!el) return;
    // Resume from saved progress
    const start = Math.floor(video?.progress_seconds || 0);
    if (start > 0) { try { el.currentTime = start; } catch (_) {} }
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);
    const onEnded = () => {
      setIsPlaying(false);
      setShowPostActions(true);
      memberApi.post(`/community/videos/${videoId}/progress`, {
        current_time_seconds: el.duration || 0,
        is_completed: true,
      }).catch(() => {});
    };
    const onMeta = () => { durationRef.current = el.duration; };
    const onEnterPip = () => setNativePipActive(true);
    const onLeavePip = () => setNativePipActive(false);
    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("ended", onEnded);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("enterpictureinpicture", onEnterPip);
    el.addEventListener("leavepictureinpicture", onLeavePip);
    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("enterpictureinpicture", onEnterPip);
      el.removeEventListener("leavepictureinpicture", onLeavePip);
    };
  }, [isDirect, video?.progress_seconds, videoId]);

  // Poll current time (1/sec)
  useEffect(() => {
    const t = setInterval(() => {
      let now = 0;
      if (isYouTube && playerRef.current?.getCurrentTime) {
        try { now = Math.floor(playerRef.current.getCurrentTime() || 0); } catch (_) {}
      } else if (isDirect && nativeVideoRef.current) {
        now = Math.floor(nativeVideoRef.current.currentTime || 0);
      }
      if (now > 0) {
        setCurrentTime(now);
        if (isPlaying && Math.abs(now - lastReportedRef.current) >= 15) {
          lastReportedRef.current = now;
          memberApi.post(`/community/videos/${videoId}/progress`, {
            current_time_seconds: now,
          }).catch(() => {});
        }
      }
    }, 1000);
    return () => clearInterval(t);
  }, [isPlaying, videoId, isYouTube, isDirect]);

  // Scroll-to-mini logic (only for YouTube — native PiP is handled by the browser)
  useEffect(() => {
    if (!isYouTube) return;
    const onScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const outOfView = rect.bottom < 80;
      setShowMini(outOfView && isPlaying);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isPlaying, isYouTube]);

  // Native PiP toggle
  const toggleNativePip = async () => {
    const el = nativeVideoRef.current;
    if (!el) return;
    try {
      if (document.pictureInPictureElement) {
        await document.exitPictureInPicture();
      } else if (document.pictureInPictureEnabled) {
        await el.requestPictureInPicture();
      }
    } catch (err) {
      console.warn("PiP failed", err);
    }
  };

  const activeCTAs = (video?.ctas || []).filter((c) => {
    if (dismissedCtas.has(c.id)) return false;
    const start = Number(c.start_time_seconds || 0);
    const end = c.end_time_seconds ? Number(c.end_time_seconds) : start + 15;
    return currentTime >= start && currentTime <= end;
  });

  if (loading) {
    return <MemberLayout><div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-amber-600 animate-spin" /></div></MemberLayout>;
  }
  if (!video) {
    return <MemberLayout><div className="p-8 text-center text-stone-400">הסרטון לא נמצא.</div></MemberLayout>;
  }

  return (
    <MemberLayout>
      <div className="max-w-[1400px] mx-auto px-0 md:px-6 lg:px-8 py-0 md:py-6" data-testid={`watch-page-${video.id}`}>
        <Link
          to="/community"
          className="hidden md:inline-flex items-center gap-1.5 text-sm text-stone-400 hover:text-amber-500 mb-4 mr-2"
          data-testid="watch-back"
        >
          <ArrowLeft className="w-4 h-4 -scale-x-100" />
          חזרה לספרייה
        </Link>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Player */}
            <div
              ref={containerRef}
              className="relative w-full aspect-video bg-black md:rounded-xl overflow-hidden border-y md:border border-stone-800 shadow-2xl shadow-black/50 group"
              data-testid="video-player"
            >
              {isDirect ? (
                <video
                  ref={nativeVideoRef}
                  src={video.video_url}
                  poster={video.thumbnail_url || undefined}
                  className="w-full h-full"
                  controls
                  controlsList="nodownload"
                  playsInline
                  data-testid="native-video-element"
                />
              ) : (
                <div ref={iframeRef} className="w-full h-full" />
              )}

              {/* Native PiP button (direct only — YouTube iframes cannot request PiP) */}
              {isDirect && !nativePipActive && (
                <button
                  onClick={toggleNativePip}
                  data-testid="pip-toggle-btn"
                  title="הפעל תצוגת תמונה-בתוך-תמונה (PiP)"
                  className="absolute top-3 left-3 w-10 h-10 rounded-full bg-black/70 hover:bg-amber-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-20"
                >
                  <PictureInPicture2 className="w-5 h-5" />
                </button>
              )}
              {isDirect && nativePipActive && (
                <div className="absolute inset-0 flex items-center justify-center bg-stone-950/85 z-10" data-testid="pip-placeholder">
                  <div className="text-center px-6">
                    <PictureInPicture2 className="w-14 h-14 text-amber-500 mx-auto mb-3 animate-pulse" />
                    <div className="font-[Rubik] font-bold text-lg text-stone-100 mb-2">מנגן בתמונה-בתוך-תמונה</div>
                    <button
                      onClick={toggleNativePip}
                      data-testid="pip-exit-btn"
                      className="mt-3 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-bold px-5 py-2 rounded-full text-sm"
                    >
                      חזרה לפלייר הרגיל
                    </button>
                  </div>
                </div>
              )}

              {/* CTA overlays */}
              <AnimatePresence>
                {activeCTAs.map((cta) => {
                  const href = resolveLink(cta);
                  const isAff = cta.use_affiliate_link !== false && !!affiliateLink?.affiliate_url;
                  return (
                    <motion.a
                      key={cta.id}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackCtaClick("cta_overlay", cta, href)}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 20 }}
                      className="absolute bottom-16 right-4 left-4 sm:left-auto sm:max-w-md bg-stone-900/95 backdrop-blur-md border border-amber-600/60 rounded-xl p-4 flex items-center gap-3 shadow-2xl"
                      data-testid={`cta-overlay-${cta.id}`}
                      data-affiliate={isAff ? "true" : "false"}
                    >
                      <Calculator className="w-6 h-6 text-amber-500 flex-shrink-0" />
                      <div className="flex-1 text-sm text-stone-100 font-medium">{cta.text}</div>
                      <ExternalLink className="w-4 h-4 text-amber-500 flex-shrink-0" />
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDismissedCtas((s) => new Set([...s, cta.id])); }}
                        className="w-6 h-6 rounded-full bg-stone-800 hover:bg-stone-700 flex items-center justify-center flex-shrink-0"
                        aria-label="סגור"
                        data-testid={`cta-dismiss-${cta.id}`}
                      >
                        <X className="w-3.5 h-3.5 text-stone-300" />
                      </button>
                    </motion.a>
                  );
                })}
              </AnimatePresence>
            </div>

            {/* Post-video actions */}
            <AnimatePresence>
              {showPostActions && (video.post_cta_buttons || []).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="bg-stone-900 border border-amber-600/40 rounded-2xl p-6 mx-4 md:mx-0"
                  data-testid="post-video-actions"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <CheckCircle2 className="w-5 h-5 text-amber-500" />
                    <h3 className="font-[Rubik] font-bold text-lg text-stone-100">
                      {video.post_cta_title || "הצעד הבא שלך"}
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {(video.post_cta_buttons || []).map((b, i) => {
                      const href = resolveLink(b);
                      const isAff = b.use_affiliate_link !== false && !!affiliateLink?.affiliate_url;
                      return (
                        <a
                          key={i}
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackCtaClick("post_cta", b, href)}
                          className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-stone-950 font-bold px-6 py-3 rounded-full transition-colors active:scale-95"
                          data-testid={`post-cta-${i}`}
                          data-affiliate={isAff ? "true" : "false"}
                        >
                          {b.text}
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Title + description */}
            <div className="px-4 md:px-0">
              <h1 className="font-[Rubik] font-black text-2xl sm:text-3xl text-stone-50 mb-3 leading-tight">
                {video.title}
              </h1>
              {video.description && (
                <p className="text-stone-300 leading-relaxed whitespace-pre-line" data-testid="watch-description">
                  {video.description}
                </p>
              )}
            </div>

            {/* Resources */}
            {(video.resources || []).length > 0 && (
              <div className="px-4 md:px-0" data-testid="watch-resources">
                <div className="text-xs font-bold text-amber-500 tracking-widest uppercase mb-3">משאבים נלווים</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {video.resources.map((r) => (
                    <a
                      key={r.id}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackCtaClick("resource", { id: r.id, text: r.title, link_url: r.url, use_affiliate_link: false }, r.url)}
                      className="flex items-center gap-3 bg-stone-900 border border-stone-800 hover:border-amber-600/50 rounded-xl p-4 transition-colors group"
                      data-testid={`resource-${r.id}`}
                    >
                      <Calculator className="w-5 h-5 text-amber-500 flex-shrink-0" />
                      <span className="flex-1 text-sm text-stone-200 font-medium">{r.title}</span>
                      <ExternalLink className="w-4 h-4 text-stone-500 group-hover:text-amber-500 transition-colors" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Up next */}
          <aside className="space-y-3 px-4 md:px-0" data-testid="up-next-list">
            <div className="text-xs font-bold text-stone-400 tracking-widest uppercase">בהמשך</div>
            {(video.up_next || []).map((v) => (
              <Link
                key={v.id}
                to={`/community/watch/${v.id}`}
                className="flex gap-3 bg-stone-900 border border-stone-800 hover:border-amber-600/40 rounded-xl overflow-hidden group transition-colors"
                data-testid={`up-next-${v.id}`}
              >
                <div className="relative w-32 aspect-video flex-shrink-0 bg-stone-800">
                  <img
                    src={v.thumbnail_url}
                    alt={v.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`; }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30">
                    <PlayCircle className="w-8 h-8 text-amber-500" />
                  </div>
                </div>
                <div className="flex-1 py-2 pl-3">
                  <div className="text-xs font-bold text-stone-100 line-clamp-2 leading-snug">{v.title}</div>
                </div>
              </Link>
            ))}
            {(video.up_next || []).length === 0 && (
              <div className="text-xs text-stone-500 py-4 text-center">אין עוד סרטונים בקטגוריה זו.</div>
            )}
          </aside>
        </div>

        <div className="text-center py-6 px-4">
          <p className="text-xs text-stone-500">
            {isDirect
              ? "💡 טיפ: לחצו על סמל ה-PiP בפינת הפלייר כדי להמשיך לצפות בזמן שאתם גוללים באתר."
              : "💡 טיפ: תוך כדי צפייה — גללו מטה כדי להמשיך להאזין ברקע במיני-פלייר."}
          </p>
        </div>
      </div>

      {/* Floating mini player (scroll-triggered) — only for YouTube; direct videos use native PiP */}
      <AnimatePresence>
        {showMini && isYouTube && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-20 md:bottom-6 left-4 md:left-6 z-40 w-56 sm:w-72 aspect-video rounded-xl overflow-hidden bg-black shadow-2xl shadow-black/60 border border-stone-700"
            data-testid="mini-player"
          >
            <div className="absolute inset-0 flex items-center justify-center bg-stone-900">
              <img
                src={video.thumbnail_url}
                alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-50"
              />
              <div className="relative z-10 text-center px-3">
                <PlayCircle className="w-10 h-10 text-amber-500 mx-auto mb-1 animate-pulse" />
                <div className="text-[10px] text-stone-300 font-bold">מנגן ברקע</div>
              </div>
            </div>
            <button
              onClick={() => { window.scrollTo({ top: 0, behavior: "smooth" }); }}
              data-testid="mini-back"
              className="absolute top-2 left-2 w-8 h-8 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center"
              aria-label="חזרה לפלייר"
              title="חזרה לפלייר"
            >
              <ArrowLeft className="w-4 h-4 text-white -scale-x-100" />
            </button>
            <button
              onClick={() => { setShowMini(false); if (playerRef.current?.pauseVideo) playerRef.current.pauseVideo(); }}
              data-testid="mini-close"
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 hover:bg-black/90 flex items-center justify-center"
              aria-label="סגור מיני-פלייר"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </MemberLayout>
  );
}
